/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/* global module */

import { applyMiddleware, compose, createStore } from 'redux'
import thunk from 'redux-thunk'

import type { CloudAppConfig } from '@modules/ui-types'
import { get as ajaxGet, post as ajaxPost } from '@modules/utils/ajax'

import { setConfig } from '../actions/config'
import { unauthorized } from '../actions/auth'
import createLogger from '../config/reduxLogger'
import unauthorizedMiddleware from '../middleware/unauthorized'
import { getReducers } from '../reducers'

import { setStore } from './store'

import type { ReduxState } from '@/types/redux'
import type { StoreEnhancer } from 'redux'

interface ConfigureStoreArgs {
  config?: CloudAppConfig
  initial?: ReduxState
}

export function configureStore({ config = {}, initial: preloadedState }: ConfigureStoreArgs = {}) {
  const reducers = getReducers(config)
  const store = createStore(reducers, preloadedState, getEnhancer(config))

  store.dispatch(setConfig(config))

  if (config.NODE_ENV !== `production`) {
    setupDevelopmentAffordances()
  }

  setStore(store)

  return store

  function setupDevelopmentAffordances() {
    if (module.hot) {
      module.hot.accept([`../reducers`], () => store.replaceReducer(getReducers(config)))
    }

    // @ts-ignore
    window.ajaxGet = ajaxGet

    // @ts-ignore
    window.ajaxPost = ajaxPost
  }
}

function getEnhancer(config: CloudAppConfig): StoreEnhancer<ReduxState> {
  const enhancements = [getMiddleware(config)]

  // instrument with the dev tools if the browser extension is being used
  // @ts-ignore
  if (window.__REDUX_DEVTOOLS_EXTENSION__) {
    // @ts-ignore
    enhancements.push(window.__REDUX_DEVTOOLS_EXTENSION__())
  }

  // @ts-ignore the TS defs don't seem to work here
  return compose(...enhancements)
}

function getMiddleware(config: CloudAppConfig) {
  const middleware = getBaseMiddleware()

  if (config.REDUX_LOGGER === true) {
    // log actions to the console as they occur
    middleware.push(createLogger())
  }

  return applyMiddleware(...middleware)
}

export function getBaseMiddleware() {
  return [
    // if user is logged in restore their login state,
    // otherwise redirect to the login page
    unauthorizedMiddleware(unauthorized),

    // allow actions to operate asynchronously
    thunk,
  ]
}
